import axios from 'axios';
import * as Sentry from '@sentry/vue';
import store from '@/store';
import { getDynamicHeaders } from '@/services/httpHeaders';
const SLOW_REQUEST_THRESHOLD_MS = 15000;
const slowRequestTimers = new WeakMap();
function createIdempotencyKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function shouldAttachIdempotencyKey(method, url) {
    if (method?.toUpperCase() !== 'POST' || !url) {
        return false;
    }
    return url.split('?')[0].replace(/^\/+/, '') === 'sales/initiate';
}
function hasHeader(headers, header) {
    if (!headers || typeof headers !== 'object') {
        return false;
    }
    if (typeof headers.has === 'function') {
        return headers.has(header);
    }
    return Object.keys(headers).some(key => key.toLowerCase() === header.toLowerCase());
}
function clearSlowRequestTimer(config) {
    if (!config) {
        return;
    }
    const timer = slowRequestTimers.get(config);
    if (timer) {
        clearTimeout(timer);
        slowRequestTimers.delete(config);
    }
}
const httpClient = axios.create({
    withCredentials: true, // Sanctum vill ha det
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CampingOnline-Version': process.env.VUE_APP_VERSION
    }
});
// Request interceptor för att lägga till dynamiska headers
httpClient.interceptors.request.use(config => {
    clearSlowRequestTimer(config);
    Object.entries(getDynamicHeaders()).forEach(([key, value]) => {
        if (value && config.headers) {
            config.headers[key] = value;
        }
    });
    if (shouldAttachIdempotencyKey(config.method, config.url) && config.headers && !hasHeader(config.headers, 'X-Idempotency-Key')) {
        config.headers['X-Idempotency-Key'] = createIdempotencyKey();
    }
    const method = config.method?.toUpperCase() || 'UNKNOWN';
    const url = config.url?.split('?')[0] || 'unknown';
    const timer = setTimeout(() => {
        slowRequestTimers.delete(config);
        console.warn(`API request to ${url} is still pending after ${SLOW_REQUEST_THRESHOLD_MS}ms`, {
            method,
            url,
            config
        });
        Sentry.captureMessage(`API request still pending after ${SLOW_REQUEST_THRESHOLD_MS}ms`, {
            level: 'warning',
            fingerprint: ['slow-api-request', method, url],
            tags: {
                type: 'slow_api_request',
                http_method: method
            },
            extra: {
                url,
                baseURL: config.baseURL,
                thresholdMs: SLOW_REQUEST_THRESHOLD_MS
            }
        });
    }, SLOW_REQUEST_THRESHOLD_MS);
    slowRequestTimers.set(config, timer);
    return config;
});
httpClient.interceptors.response.use(response => {
    clearSlowRequestTimer(response.config);
    if (response.data.snackbar) {
        store.dispatch('snackbars/createSnackbar', response.data.snackbar, { root: true });
    }
    if (response.data.print) {
        store.dispatch('print/print', response.data.print, { root: true });
    }
    store.commit('user/SET_HAS_TIMEOUT_ERROR', false);
    return response;
}, error => {
    clearSlowRequestTimer(error.config);
    console.log('intercept error', error.response);
    if (error.response === undefined) {
        store.commit('user/SET_HAS_TIMEOUT_ERROR', true);
        store.dispatch('user/deactivateAuthSession');
        store.dispatch('snackbars/createSnackbar', {
            color: 'error',
            text: 'Servern svarade inte, kontrollera din anslutning'
        }, { root: true });
    }
    else if (store.getters['user/activeAuthSession']?.domain === '' || error.response.status === 401) {
        store.commit('user/SET_HAS_TIMEOUT_ERROR', false);
        store.dispatch('user/logout');
    }
    else if (error.response.status === 403 && error.response.data.error_code === 'electron_update_required') {
        // Full electron-uppdatering krävs - servern vet att detta är en electron-klient
        store.commit('user/SET_HAS_VERSION_ERROR', true);
        store.commit('SET_UPDATE_SOURCE', 'forced');
        store.commit('SET_UPDATE_URL', error.response.data.url);
        store.commit('SET_UPDATE_TYPE', 'electron');
        store.commit('SET_SHOW_UPDATE_DIALOG', true);
    }
    else if (error.response.status === 403 && error.response.data.error_code === 'update_required') {
        // PWA-uppdatering krävs
        store.commit('user/SET_HAS_VERSION_ERROR', true);
        store.commit('SET_UPDATE_SOURCE', 'forced');
        store.commit('SET_UPDATE_URL', error.response.data.url);
        store.commit('SET_UPDATE_TYPE', 'pwa');
        store.commit('SET_SHOW_UPDATE_DIALOG', true);
    }
    else if (error.response.status === 0) {
        // error.response.status === 0 betyder ingen response alls så nätverket är nere
        // Troligen ett nätverksfel, försök igen
        // Ska fungera likadant för yttre kod som skickar api anropen
        error.config.retries = error.config.retries || 0;
        error.config.retries += 1;
        if (error.config.retries < 3) {
            console.log('Nätverksfel, försöker igen');
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    httpClient(error.config)
                        .then(resolve)
                        .catch(err => {
                        reject(err);
                    });
                }, 1000 * error.config.retries); // Väntar lite, med inbyggd backoff
            });
        }
        else {
            console.log('Har försökt 3 gånger, ger upp');
            store.dispatch('snackbars/createSnackbar', {
                color: 'error',
                text: 'Problem med internetuppkopplingen'
            }, { root: true });
            return Promise.reject(error); // Ger upp och bryter rekursionen
        }
    }
    else if (error.response.status !== 524) {
        // 524 är en cloudflare timeout, efter 100s svarar cloudflare åt oss med 524
        store.dispatch('snackbars/createSnackbar', {
            color: 'error',
            text: `Ett oväntat fel inträffade. Felkod: ${error.response.status}`
        }, { root: true });
    }
    else {
        // har response, men inte 401, 403 eller 524
    }
    return Promise.reject(error);
});
/**
 * For use with TanStack query since it needs thrown errors to enter error state.
 *
 * Checks if the API response has status "error" and throws an Error if so.
 * Use this in .then() handlers for API calls.
 *
 * @throws {Error} If status is "error", throws an Error with the error message
 */
export function assertSuccessResponse(response) {
    if (response.data && response.data.status === 'error') {
        throw new Error(response.data.message ||
            response.data.error_message ||
            'Ett oväntat fel inträffade');
    }
    return response.data.data;
}
export default {
    httpClient,
    assertSuccessResponse
};
